import React from 'react';
import { Plus, Search, Package, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import SafeImage from '../../SafeImage';

// The Inventory tab's product list: search/filter bar, paginated table, and
// row-level actions (edit, delete, toggle active). All state and handlers
// come from useInventory — this component is presentation only.
const InventoryTab = ({ inventory, dashboardStats }) => {
  const {
    products, loadingProducts, productFilter, setProductFilter,
    productPage, setProductPage, PRODUCTS_PER_PAGE,
    getFilteredProducts, getPaginatedProducts,
    openAddProduct, openViewProduct, handleEditProduct, handleDeleteProduct, handleToggleActive,
  } = inventory;

  const filteredCount = getFilteredProducts().length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Inventory Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            {productFilter === 'all' ? 'Total Products' :
             productFilter === 'low_stock' ? 'Low Stock Products' :
             productFilter === 'active' ? 'Active Products' :
             productFilter === 'inactive' ? 'Inactive Products' :
             'Products'}: <span className="font-semibold text-[#d44100]">{filteredCount}</span>
            {productFilter !== 'all' && (
              <span className="ml-2 text-xs text-gray-500">(of {products.length} total)</span>
            )}
            {productFilter === 'all' && dashboardStats?.totalProducts && dashboardStats.totalProducts !== products.length && (
              <span className="ml-2 text-xs text-gray-500">(Dashboard shows: {dashboardStats.totalProducts})</span>
            )}
          </p>
        </div>
        <button
          onClick={openAddProduct}
          className="bg-[#d44100] text-white px-4 py-2 rounded-lg hover:bg-[#b83700] flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={productFilter}
            onChange={(e) => { setProductFilter(e.target.value); setProductPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
          >
            <option value="all">All Products</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="low_stock">Low Stock</option>
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loadingProducts ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d44100]"></div>
          </div>
        ) : filteredCount === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {productFilter === 'low_stock' ? 'No low stock products' :
               productFilter === 'active' ? 'No active products' :
               productFilter === 'inactive' ? 'No inactive products' :
               'No products'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {productFilter === 'low_stock' ? 'All products have sufficient stock.' :
               productFilter === 'active' ? 'No products are currently active.' :
               productFilter === 'inactive' ? 'No products are currently inactive.' :
               'Get started by creating a new product.'}
            </p>
            {productFilter !== 'all' && (
              <div className="mt-4">
                <button onClick={() => setProductFilter('all')} className="text-[#d44100] hover:text-[#b83700] font-medium">
                  View All Products
                </button>
              </div>
            )}
            {productFilter === 'all' && (
              <div className="mt-6">
                <button
                  onClick={openAddProduct}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#d44100] hover:bg-[#b83700]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getPaginatedProducts().map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openViewProduct(product)}
                        className="flex items-center text-left group"
                        title="View product"
                      >
                        <SafeImage src={product.image || '/images/placeholder.jpg'} alt={product.name} className="h-10 w-10 rounded-lg object-cover mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 group-hover:text-[#d44100] transition-colors">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.category}</div>
                        </div>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.sku || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          product.stockQuantity <= 5 ? 'bg-red-100 text-red-800' :
                          product.stockQuantity <= 10 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {product.stockQuantity}
                        </span>
                        {product.stockQuantity <= 5 && <span className="ml-2 text-xs text-red-600">Low Stock</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">KSh {product.price.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => handleToggleActive(product.id, product.isActive)}
                          className={`p-1 rounded ${product.isActive ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                          title={product.isActive ? 'Deactivate Product' : 'Activate Product'}
                        >
                          {product.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => handleEditProduct(product)} className="text-[#d44100] hover:text-[#b83700]" title="Edit Product">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteProduct(product.id, product.name)} className="text-red-600 hover:text-red-700" title="Delete Product">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination controls */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-sm text-gray-600">
              <div>
                Showing{' '}
                <span className="font-semibold">{Math.min((productPage - 1) * PRODUCTS_PER_PAGE + 1, filteredCount)}</span>{' '}
                to{' '}
                <span className="font-semibold">{Math.min(productPage * PRODUCTS_PER_PAGE, filteredCount)}</span>{' '}
                of{' '}
                <span className="font-semibold">{filteredCount}</span>{' '}
                products
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setProductPage((prev) => Math.max(1, prev - 1))}
                  disabled={productPage === 1}
                  className={`px-3 py-1 rounded-md border text-sm ${productPage === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <span>
                  Page <span className="font-semibold">{productPage}</span> of{' '}
                  <span className="font-semibold">{Math.max(1, Math.ceil(filteredCount / PRODUCTS_PER_PAGE))}</span>
                </span>
                <button
                  onClick={() => setProductPage((prev) => (prev < Math.ceil(filteredCount / PRODUCTS_PER_PAGE) ? prev + 1 : prev))}
                  disabled={productPage >= Math.ceil(filteredCount / PRODUCTS_PER_PAGE)}
                  className={`px-3 py-1 rounded-md border text-sm ${productPage >= Math.ceil(filteredCount / PRODUCTS_PER_PAGE) ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryTab;
